/** Writs of ejectment: board by stage and set-out calendar. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, useRouter } from '../shared/router.tsx';
import { LandlordLinkBox, OUTCOME_LABEL, ResultDialog, ScheduleDialog, fmtTimeOnly, fmtWhen, localDay, setAgencyTz } from '../shared/setout.tsx';
import { Badge, Card, Empty, ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

const STAGES = [
  { key: 'to_serve', label: 'To serve', hint: 'Not served or posted yet' },
  { key: 'waiting', label: '24-hour wait', hint: 'Served/posted; set-out locked' },
  { key: 'ready', label: 'Ready to schedule', hint: 'Wait is over' },
  { key: 'scheduled', label: 'Scheduled', hint: 'Set-out on the calendar' },
  { key: 'done', label: 'Done', hint: 'Last 30 days' },
] as const;

function untilText(t: string) {
  const ms = +new Date(t) - Date.now();
  if (ms <= 0) return 'now';
  const mins = Math.ceil(ms / 60e3);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h} h ${m} min` : `${m} min`;
}

/** Actions on a set-out (board card, calendar, paper screen). */
function useSetOutActions(reload: () => void) {
  const [dialog, setDialog] = useState<null | { kind: 'schedule' | 'result' | 'link'; card: any; link?: string }>(null);
  const a = useAction();
  const cancel = (card: any, prefill?: string | null) => {
    const reason = window.prompt('Why is the set-out cancelled? (e.g. tenant paid, writ stayed)', prefill ?? '')?.trim();
    if (reason) a.run(async () => (await api.post(`/api/setouts/${card.setOut.id}/cancel`, { reason }), reload()));
  };
  const keep = (card: any) => a.run(async () => (await api.post(`/api/setouts/${card.setOut.id}/keep`), reload()));
  const newLink = (card: any) =>
    a.run(async () => {
      const r = await api.post<{ landlordLink: string }>(`/api/setouts/${card.setOut.id}/landlord-link`);
      setDialog({ kind: 'link', card, link: r.landlordLink });
    });
  const node = dialog && (
    <>
      {dialog.kind === 'schedule' && (
        <ScheduleDialog
          paper={{ id: dialog.card.paperId, defendant: dialog.card.defendant, address: dialog.card.address, plaintiff: dialog.card.plaintiff, unlockAt: dialog.card.unlockAt }}
          current={dialog.card.setOut?.status === 'scheduled' ? dialog.card.setOut : null}
          minDeputies={dialog.card.minDeputies ?? 2}
          onClose={() => setDialog(null)}
          onDone={() => (setDialog(null), reload())}
        />
      )}
      {dialog.kind === 'result' && <ResultDialog setOut={dialog.card.setOut} title={dialog.card.defendant} onClose={() => setDialog(null)} onDone={() => (setDialog(null), reload())} />}
      {dialog.kind === 'link' && (
        <div className="modal-back" role="dialog" aria-label="Landlord link">
          <div className="modal wide">
            <h2>New landlord link · {dialog.card.defendant}</h2>
            <p className="small muted">The old link no longer works.</p>
            <LandlordLinkBox link={dialog.link!} landlord={dialog.card.setOut.landlord} when={dialog.card.setOut.scheduledFor} address={dialog.card.address} />
            <button className="btn primary" onClick={() => setDialog(null)}>
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
  return { node, setDialog, cancel, keep, newLink, error: a.error, busy: a.busy };
}

function SetOutLine({ so }: { so: any }) {
  return (
    <div className="so-line small">
      <div>
        <strong>{fmtWhen(so.scheduledFor)}</strong>
      </div>
      <div className="muted">{so.deputies.map((d: any) => d.name.replace(/^Deputy /, '')).join(' & ')}</div>
      <div className="row gap-sm wrap">
        {so.landlord.cancelRequestedAt ? (
          <Badge tone="bad">Landlord asked to cancel</Badge>
        ) : so.landlord.confirmedAt ? (
          <Badge tone="ok">Landlord confirmed</Badge>
        ) : (
          <Badge tone="warn">Landlord not confirmed</Badge>
        )}
        {so.needsResult && <Badge tone="bad">Result not recorded</Badge>}
      </div>
    </div>
  );
}

function BoardCard({ c, can, act }: { c: any; can: boolean; act: ReturnType<typeof useSetOutActions> }) {
  const so = c.setOut;
  return (
    <div className={`so-card ${c.stage}${so?.landlord?.cancelRequestedAt || so?.needsResult ? ' alert' : ''}`}>
      <div className="row between gap-sm">
        <Link to={`/papers/${c.paperId}`} className="so-name">
          {c.defendant}
        </Link>
        <span className="mono small muted">{c.number}</span>
      </div>
      <div className="small">{c.address}</div>
      {c.plaintiff && <div className="small muted">Landlord: {c.plaintiff}</div>}
      {c.stage === 'to_serve' && <div className="small">{c.deputy ? `With ${c.deputy}` : 'Not assigned'}</div>}
      {c.stage === 'waiting' && (
        <div className="small">
          Posted {fmtWhen(c.servedAt)} · <strong>unlocks in {untilText(c.unlockAt)}</strong>
        </div>
      )}
      {c.stage === 'ready' && <div className="small">Served {fmtWhen(c.servedAt)}</div>}
      {c.stage === 'scheduled' && so && <SetOutLine so={so} />}
      {c.stage === 'scheduled' && so?.landlord?.cancelRequestedAt && <div className="small so-ask">“{so.landlord.cancelReason}”</div>}
      {c.stage === 'done' && (
        <div className="small">
          {so ? (
            <>
              <strong>{OUTCOME_LABEL[so.outcome] ?? so.status}</strong> {fmtWhen(so.completedAt ?? so.cancelledAt)}
              {so.cancelReason && <div className="muted">{so.cancelReason}</div>}
            </>
          ) : (
            <strong>{c.doneReason}</strong>
          )}
        </div>
      )}
      {can && (
        <div className="row gap-sm wrap so-actions">
          {c.stage === 'ready' && (
            <button className="btn sm primary" onClick={() => act.setDialog({ kind: 'schedule', card: c })}>
              Schedule set-out
            </button>
          )}
          {c.stage === 'scheduled' && so?.landlord?.cancelRequestedAt && (
            <>
              <button className="btn sm warn" onClick={() => act.cancel(c, so.landlord.cancelReason)}>
                Cancel set-out
              </button>
              <button className="btn sm" onClick={() => act.keep(c)}>
                Keep it
              </button>
            </>
          )}
          {c.stage === 'scheduled' && (
            <>
              <button className="btn sm primary" onClick={() => act.setDialog({ kind: 'result', card: c })}>
                Record result
              </button>
              <button className="btn sm" onClick={() => act.setDialog({ kind: 'schedule', card: c })}>
                Reschedule
              </button>
              <button className="btn sm ghost" onClick={() => act.newLink(c)}>
                Landlord link
              </button>
              {!so?.landlord?.cancelRequestedAt && (
                <button className="btn sm ghost" onClick={() => act.cancel(c)}>
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Board({ reloadKey }: { reloadKey: number }) {
  const { can } = useAuth();
  const r = useLoad(() => api.get<any>('/api/setouts/board'), [reloadKey]);
  const act = useSetOutActions(() => r.reload());
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const cards = r.data.cards.map((c: any) => ({ ...c, minDeputies: r.data.minDeputies }));
  return (
    <>
      <ErrorBox error={act.error} />
      <div className="so-board">
        {STAGES.map((s) => {
          const list = cards.filter((c: any) => c.stage === s.key);
          return (
            <section key={s.key} className={`so-col ${s.key}`} aria-label={s.label}>
              <header>
                <strong>{s.label}</strong> <span className="so-count">{list.length}</span>
                <div className="small muted">{s.hint}</div>
              </header>
              {list.length ? list.map((c: any) => <BoardCard key={c.paperId} c={c} can={can('setout.schedule')} act={act} />) : <div className="small muted so-empty">None</div>}
            </section>
          );
        })}
      </div>
      {act.node}
    </>
  );
}

function monthGrid(month: string) {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1, 1));
  const days: (string | null)[] = Array(first.getUTCDay()).fill(null);
  for (let d = 1; d <= new Date(Date.UTC(y, m, 0)).getUTCDate(); d++) days.push(`${month}-${String(d).padStart(2, '0')}`);
  while (days.length % 7) days.push(null);
  return days;
}
const shiftMonth = (month: string, n: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

function Calendar({ reloadKey }: { reloadKey: number }) {
  const { can } = useAuth();
  const today = localDay(new Date());
  const [month, setMonth] = useState(today.slice(0, 7));
  const [pick, setPick] = useState<string | null>(null);
  const days = monthGrid(month);
  const from = days.find(Boolean)!;
  const to = [...days].reverse().find(Boolean)!;
  const r = useLoad(() => api.get<any>(`/api/setouts?from=${from}&to=${to}`), [month, reloadKey]);
  const act = useSetOutActions(() => r.reload());
  const on = (d: string) => (r.data?.setOuts ?? []).filter((s: any) => s.day === d);
  const sel = pick ? on(pick) : [];
  const label = new Date(`${month}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return (
    <>
      <div className="row between wrap gap">
        <div className="row gap-sm">
          <button className="btn sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Previous month">
            ‹
          </button>
          <strong className="so-month">{label}</strong>
          <button className="btn sm" onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Next month">
            ›
          </button>
          <button className="btn sm ghost" onClick={() => setMonth(today.slice(0, 7))}>
            Today
          </button>
        </div>
        <span className="small muted">Schedule a set-out from the Board (Ready to schedule) or the paper.</span>
      </div>
      <div className="so-cal" role="grid" aria-label={`Set-outs in ${label}`}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="so-dow">
            {d}
          </div>
        ))}
        {days.map((d, i) =>
          d ? (
            <button key={d} className={`so-day${d === today ? ' today' : ''}${d === pick ? ' sel' : ''}${on(d).length ? ' has' : ''}`} onClick={() => setPick(d)} aria-label={`${d}: ${on(d).length} set-outs`}>
              <span className="so-dn">{Number(d.slice(8))}</span>
              {on(d)
                .slice(0, 3)
                .map((s: any) => (
                  <span key={s.id} className={`so-chip ${s.status}${s.landlord.cancelRequestedAt ? ' alert' : ''}`}>
                    {fmtTimeOnly(s.scheduledFor)} {s.defendant.split(' ').slice(-1)[0]}
                  </span>
                ))}
              {on(d).length > 3 && <span className="small muted">+{on(d).length - 3} more</span>}
            </button>
          ) : (
            <div key={`x${i}`} className="so-day blank" />
          ),
        )}
      </div>
      {pick && (
        <Card title={new Date(`${pick}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })}>
          {sel.length === 0 ? (
            <Empty>No set-outs this day.</Empty>
          ) : (
            sel.map((s: any) => {
              const c = { paperId: s.paperId, defendant: s.defendant, address: s.address, number: s.number, setOut: s, stage: s.status === 'scheduled' ? 'scheduled' : 'done' };
              return (
                <div key={s.id} className="so-dayrow">
                  <BoardCard c={c} can={can('setout.schedule')} act={act} />
                </div>
              );
            })
          )}
        </Card>
      )}
      <ErrorBox error={act.error} />
      {act.node}
    </>
  );
}

export function EjectmentsPage() {
  const { me } = useAuth();
  setAgencyTz(me?.agency.timezone);
  const { path, navigate } = useRouter();
  const tab = new URLSearchParams(path.split('?')[1] ?? '').get('view') === 'calendar' ? 'calendar' : 'board';
  const [k] = useState(0);
  return (
    <div className="stack">
      <div>
        <h1>Writs of ejectment</h1>
        <p className="muted">Every writ from posting to set-out. Set-outs need two deputies and can’t be scheduled until 24 hours after service or posting.</p>
      </div>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'board'} className={tab === 'board' ? 'active' : ''} onClick={() => navigate('/ejectments')}>
          Board
        </button>
        <button role="tab" aria-selected={tab === 'calendar'} className={tab === 'calendar' ? 'active' : ''} onClick={() => navigate('/ejectments?view=calendar')}>
          Set-out calendar
        </button>
      </div>
      {tab === 'board' ? <Board reloadKey={k} /> : <Calendar reloadKey={k} />}
    </div>
  );
}

/** Card on a writ's paper screen. */
export function EjectmentCard({ paper, setOuts, servedAt, onChanged }: { paper: any; setOuts: any[]; servedAt: string | null; onChanged: () => void }) {
  const { can, me } = useAuth();
  setAgencyTz(me?.agency.timezone);
  const act = useSetOutActions(onChanged);
  const current = setOuts.find((s) => s.status === 'scheduled') ?? null;
  const last = current ?? setOuts.find((s) => s.status !== 'replaced') ?? null;
  const unlockAt = servedAt ? new Date(+new Date(servedAt) + 24 * 3600e3).toISOString() : null;
  const card = { paperId: paper.id, defendant: paper.defendant, address: paper.address, plaintiff: paper.plaintiff, unlockAt, setOut: current, minDeputies: 2 };
  const locked = !servedAt || +new Date(unlockAt!) > Date.now();
  return (
    <Card title="Set-out">
      {!servedAt ? (
        <p className="small muted">The writ has to be served or posted first. Then there’s a 24-hour wait.</p>
      ) : last?.status === 'completed' || last?.status === 'cancelled' ? (
        <Notice tone={last.status === 'completed' ? 'ok' : 'warn'}>
          <strong>{OUTCOME_LABEL[last.outcome] ?? last.status}</strong> {fmtWhen(last.completedAt ?? last.cancelledAt)}
          {last.deputiesPresent?.length ? ` · ${last.deputiesPresent.map((d: any) => d.name).join(', ')}` : ''}
          {last.locksChanged ? ' · locks changed' : ''}
          {last.notes ? <div className="small">{last.notes}</div> : null}
          {last.cancelReason ? <div className="small">{last.cancelReason}</div> : null}
        </Notice>
      ) : current ? (
        <>
          <SetOutLine so={current} />
          {current.landlord.cancelRequestedAt && <Notice tone="bad">Landlord asked to cancel: “{current.landlord.cancelReason}”</Notice>}
          {current.equipment && <div className="small">Equipment: {current.equipment}</div>}
        </>
      ) : locked ? (
        <p className="small">Locked until {fmtWhen(unlockAt!)} (24-hour wait after service/posting).</p>
      ) : (
        <p className="small">Ready to schedule.</p>
      )}
      {can('setout.schedule') && servedAt && last?.status !== 'completed' && (
        <div className="row gap-sm wrap">
          {!current && !locked && last?.status !== 'cancelled' && (
            <button className="btn sm primary" onClick={() => act.setDialog({ kind: 'schedule', card })}>
              Schedule set-out
            </button>
          )}
          {current && (
            <>
              <button className="btn sm primary" onClick={() => act.setDialog({ kind: 'result', card })}>
                Record result
              </button>
              <button className="btn sm" onClick={() => act.setDialog({ kind: 'schedule', card })}>
                Reschedule
              </button>
              <button className="btn sm ghost" onClick={() => act.newLink(card)}>
                Landlord link
              </button>
              <button className="btn sm ghost" onClick={() => act.cancel(card, current.landlord.cancelReason)}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
      <ErrorBox error={act.error} />
      {act.node}
    </Card>
  );
}
